import { Component, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { MatRadioChange } from '@angular/material/radio';
import { Router } from '@angular/router';
import { SiteStatusEnum } from '@app/enums/site-status.enum';
import { ModalResult } from '@app/v2/enums/modal-result.enum';
import { SiteService } from '@app/v2/services/site/site.service';
import { CardMode } from '@components/card/card.component';
import { MedpaceSiteAddressComponent } from '@components/molecules/forms/site-address/site-address.component';
import { MedpaceSiteShippingAddressComponent } from '@components/molecules/forms/site-shipping-address/site-shipping-address.component';
import {
    MdsButtonComponent,
    MdsCardComponent,
    MdsCardContentComponent,
    MdsCheckboxComponent,
    MdsDialogActionsComponent,
    MdsDialogComponent,
    MdsDialogContentComponent,
    MdsRadioButton,
    MdsRadioGroupComponent,
    MdsTextFieldComponent,
} from '@medpacesoftwaredevelopment/designsystem';
import { IUser } from '@models/interfaces/iUser';
import { Address, Site, SiteCRC, SiteDataModel } from '@models/site';
import { AdminSiteServices } from '@services/admin/admin-site.service';
import { SiteStateService } from '@services/state-management/site-state.service';
import { transformToSiteDataModel } from '@services/transforms/site-transform';
import { toStringOrFallback } from '@utility/utility';
import { emailValidator, maxLengthValidator, notWhitespaceValidator } from '@utility/utility.validators';
import { switchMap, take, tap } from 'rxjs';

@Component({
    selector: 'site-register-modal',
    standalone: true,
    templateUrl: './site-register-modal.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MatDialogClose,
        MdsDialogComponent,
        MdsDialogContentComponent,
        MdsDialogActionsComponent,
        MdsTextFieldComponent,
        MdsRadioGroupComponent,
        MdsButtonComponent,
        MdsCardComponent,
        MdsCardContentComponent,
        MdsCheckboxComponent,
        MedpaceSiteAddressComponent,
        MedpaceSiteShippingAddressComponent,
    ],
})
export class SiteRegisterModalComponent {
    private readonly siteServiceV2 = inject(SiteService);
    private readonly router = inject(Router);
    private readonly stateService = inject(SiteStateService);
    private readonly siteService = inject(AdminSiteServices);
    readonly dialogRef = inject(MatDialogRef<SiteRegisterModalComponent>);
    readonly data: number = inject(MAT_DIALOG_DATA);

    readonly hasSupportingCrc = signal(false);
    readonly site = signal<Site>(null);
    readonly primaryCRC = signal<SiteCRC>(null);

    readonly addressFormGroup: FormGroup = new FormGroup({});
    readonly addressCardMode = signal<CardMode>('edit');

    readonly supportingCrcFormArray = new FormArray<
        FormGroup<{
            firstNameCtrl: FormControl<string>;
            lastNameCtrl: FormControl<string>;
            emailCtrl: FormControl<string>;
        }>
    >([]);

    readonly shippingAddressFormGroup: FormGroup = new FormGroup({});
    readonly shippingAddressCardMode = signal<CardMode>('edit');

    readonly baseFormGroup = new FormGroup({
        phoneCtrl: new FormControl('', [Validators.required, notWhitespaceValidator(), maxLengthValidator(20)]),
        supportingRadioButtons: new FormControl<boolean>(false),
        verifyCheckboxControl: new FormControl<boolean>(false, [Validators.requiredTrue]),
    });

    get phoneCtrl() {
        return this.baseFormGroup.controls.phoneCtrl;
    }

    readonly supportingCrcRadioButtons: MdsRadioButton[] = [
        { label: 'Yes', value: true, name: 'yesCrc', id: 'yesCrc' },
        { label: 'No', value: false, name: 'noCrc', id: 'noCrc' },
    ];

    private readonly _init = this.stateService.setSite(this.data);

    private readonly siteFromState = toSignal(this.stateService.getSite());

    private readonly siteEffect = effect(() => {
        const siteResult = this.siteFromState();
        if (siteResult) {
            this.site.set(siteResult);
            this.primaryCRC.set(siteResult.siteCRCs?.find((crc) => crc.isPrimary));
            this.phoneCtrl.setValue(this.primaryCRC()?.user?.phoneNumber ?? '');
        }
    });

    protected cancel() {
        this.dialogRef.close(ModalResult.Cancel);
    }

    protected validAndClose() {
        if (this.supportingCrcFormArray.length > 0 && !this.hasSupportingCrc()) {
            this.supportingCrcFormArray.clear();
        }

        const allFormsValid =
            this.baseFormGroup.valid &&
            this.addressFormGroup.valid &&
            this.shippingAddressFormGroup.valid &&
            this.supportingCrcFormArray.valid;

        if (allFormsValid) {
            this.updateSiteAndChangeStatus();
            this.dialogRef.close(ModalResult.Okay);
        } else {
            this.baseFormGroup.markAllAsTouched();
            this.addressFormGroup.markAllAsTouched();
            this.shippingAddressFormGroup.markAllAsTouched();
            this.supportingCrcFormArray.markAllAsTouched();
        }
    }

    private updateSiteAndChangeStatus() {
        this.updateSiteModel();
        const model = transformToSiteDataModel(this.site());
        this.addCrcToSiteModel(model);

        this.siteService
            .changeSiteStatus(SiteStatusEnum[SiteStatusEnum.Registered], this.site().id, model)
            .pipe(
                switchMap(() => this.siteServiceV2.numberOfSitesInStatus.reload$(SiteStatusEnum.PreRegistered)),
                take(1),
                tap((numberOfPreRegs) => {
                    if (numberOfPreRegs > 0) {
                        this.router.navigate(['dashboard/notification'], { replaceUrl: true });
                    } else {
                        this.router.navigate(['dashboard'], { replaceUrl: true });
                    }
                })
            )
            .subscribe();
    }

    private addCrcToSiteModel(model: SiteDataModel) {
        this.supportingCrcFormArray.controls.forEach((supportingCRC) => {
            model.siteCrcs.push(<SiteCRC>{
                user: <IUser>{
                    firstName: supportingCRC.controls.firstNameCtrl.value,
                    lastName: supportingCRC.controls.lastNameCtrl.value,
                    emailAddress: supportingCRC.controls.emailCtrl.value,
                },
                isPrimary: false,
                userId: 0,
                siteId: this.site().id,
            });
        });
    }

    private updateSiteModel() {
        const currentSite = this.site();
        const primaryAddressFormGroup = this.addressFormGroup.get('addressFormGroup');
        const address: Address = {
            line1: primaryAddressFormGroup?.get('line1Control')?.value,
            line2: primaryAddressFormGroup?.get('line2Control')?.value,
            city: primaryAddressFormGroup?.get('cityControl')?.value,
            stateOrProvince: primaryAddressFormGroup?.get('stateOrProvinceControl')?.value,
            postalCode: primaryAddressFormGroup?.get('postalCodeControl')?.value,
            country: primaryAddressFormGroup?.get('countryControl')?.value,
            region: primaryAddressFormGroup?.get('regionControl')?.value,
        };

        const shippingAddFormGroup = this.shippingAddressFormGroup.get('addressFormGroup');
        const shippingAddressSameAsPrimary =
            this.shippingAddressFormGroup.get('addressSameAsPrimary')?.value === 'true';
        const shippingAddress: Address = {
            line1: shippingAddFormGroup?.get('sline1Control')?.value,
            line2: shippingAddFormGroup?.get('sline2Control')?.value,
            city: shippingAddFormGroup?.get('scityControl')?.value,
            stateOrProvince: shippingAddFormGroup?.get('sstateOrProvinceControl')?.value,
            postalCode: shippingAddFormGroup?.get('spostalCodeControl')?.value,
            country: shippingAddFormGroup?.get('scountryControl')?.value,
            region: shippingAddFormGroup?.get('sregionControl')?.value,
        };

        currentSite.isShippingAddressSameAsPrimary = shippingAddressSameAsPrimary;
        currentSite.shippingAddress = shippingAddress;
        currentSite.address = address;
        this.primaryCRC().user.phoneNumber = this.phoneCtrl.value;
        currentSite.isReviewedByCRC = true;
    }

    protected selectSupportingCrcValue(event: MatRadioChange) {
        this.hasSupportingCrc.set(event.value === true);

        if (this.hasSupportingCrc() && this.supportingCrcFormArray.length === 0) {
            this.addSupportingCRC();
        }
    }

    protected toStringOrFallback(object: Object, fallback: string) {
        return toStringOrFallback(object, fallback);
    }

    protected removeSupportingCRC(index: number) {
        this.supportingCrcFormArray.removeAt(index);
    }

    protected addSupportingCRC() {
        this.supportingCrcFormArray.push(this.createSupportingCrcFormGroup());
    }

    private createSupportingCrcFormGroup(): FormGroup<{
        firstNameCtrl: FormControl<string>;
        lastNameCtrl: FormControl<string>;
        emailCtrl: FormControl<string>;
    }> {
        return new FormGroup({
            firstNameCtrl: new FormControl('', [Validators.required, notWhitespaceValidator(), maxLengthValidator(50)]),
            lastNameCtrl: new FormControl('', [Validators.required, notWhitespaceValidator(), maxLengthValidator(50)]),
            emailCtrl: new FormControl('', [
                Validators.required,
                notWhitespaceValidator(),
                emailValidator,
                maxLengthValidator(60),
            ]),
        });
    }
}
